import React from 'react'
import { MenuIcon } from '../icons/icons'
import bizFirstLogo from '../../../bizfirst-common-themes/images/BizFirstAiLogoMobileDark.png'

export interface AppHeaderProps {
  /** Main title — accepts a string or a ReactNode for styled/custom markup */
  title: React.ReactNode
  /** Optional secondary description; shown as the title's tooltip */
  subtitle?: string
  /** Whether to render the sidebar toggle button (default: true) */
  sidebarToggle?: boolean
  /** Current collapsed state of the sidebar — controls toggle tooltip */
  navCollapsed?: boolean
  /** Called when the sidebar toggle button is clicked */
  onToggleSidebar?: () => void
  /** Content rendered in the right side of the header (e.g. <BizFirstLinks />) */
  rightContent?: React.ReactNode
}

/**
 * AppHeader Component
 *
 * Shared application header with logo, menu toggle, title, and right content area.
 * Uses hardcoded BizFirst AI logo.
 *
 * For dynamic branding (logo from API), see BrandingInitializer in @bizfirst/common-auth-react
 *
 * @example
 * ```tsx
 * <AppHeader
 *   title="MyApp"
 *   sidebarToggle={true}
 *   navCollapsed={navCollapsed}
 *   onToggleSidebar={toggleNav}
 *   rightContent={<BizFirstLinks />}
 * />
 * ```
 */
export function AppHeader({
  title,
  subtitle,
  sidebarToggle = true,
  navCollapsed = true,
  onToggleSidebar,
  rightContent,
}: AppHeaderProps): React.ReactElement {
  return (
    <header className="app-header">
      <div className="app-header-brand">
        {sidebarToggle && (
          <button
            className="nav-toggle"
            onClick={onToggleSidebar}
            title={navCollapsed ? 'Expand navigation' : 'Collapse navigation'}
            aria-label="Toggle navigation"
            aria-expanded={!navCollapsed}
          >
            <MenuIcon />
          </button>
        )}
        <div className="app-logo">
          <img src={bizFirstLogo} alt="BizFirst AI" className="logo-image" />
        </div>
        <div>
          <h1 className="header-title" title={subtitle}>{title}</h1>
        </div>
      </div>
      {rightContent && <div className="app-header-right">{rightContent}</div>}
    </header>
  )
}

export default AppHeader
